import { useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  ArrowLeftIcon,
  BeakerIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline'
import {
  GET_RAW_MATERIAL,
  GET_RAW_MATERIAL_PRICE_HISTORY,
  GET_RAW_MATERIALS,
} from '../../graphql/costing-queries'
import {
  UPDATE_RAW_MATERIAL,
  SET_RAW_MATERIAL_PRICE,
} from '../../graphql/costing-mutations'
import { RawMaterial, RawMaterialPriceEntry } from '../../types/costing'
import {
  formatCost,
  formatCostPerUnit,
  formatDate,
  eurosToMillicents,
  millicentsToEuros,
} from '../../lib/costingUtils'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'

type Tab = 'info' | 'pricing'

const RawMaterialDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const materialId = parseInt(id!)
  const [activeTab, setActiveTab] = useState<Tab>('info')

  const { data, loading, error } = useQuery(GET_RAW_MATERIAL, {
    variables: { id: materialId },
    skip: !id,
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !data?.rawMaterial) {
    return <Navigate to="/costing/raw-materials" replace />
  }

  const material: RawMaterial = data.rawMaterial

  const tabs = [
    { id: 'info' as Tab, name: 'Info', icon: BeakerIcon },
    { id: 'pricing' as Tab, name: 'Pricing', icon: CurrencyEuroIcon },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/costing/raw-materials"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{material.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Unit: <span className="font-medium">{material.unit}</span>
              {' · '}
              Cost: <span className="font-medium">{formatCostPerUnit(material.current_cost_per_unit_millicents, material.unit)}</span>
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          material.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {material.active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'info' && <InfoTab material={material} materialId={materialId} />}
        {activeTab === 'pricing' && <PricingTab materialId={materialId} material={material} />}
      </div>
    </div>
  )
}

/* ─── Info Tab ─────────────────────────────────────────────────────────────── */

const InfoTab = ({ material, materialId }: { material: RawMaterial; materialId: number }) => {
  const [name, setName] = useState(material.name)
  const [notes, setNotes] = useState(material.notes ?? '')
  const [active, setActive] = useState(material.active)
  const [trackInventory, setTrackInventory] = useState(material.track_inventory ?? false)

  const [updateRawMaterial, { loading }] = useMutation(UPDATE_RAW_MATERIAL, {
    refetchQueries: [
      { query: GET_RAW_MATERIAL, variables: { id: materialId } },
      { query: GET_RAW_MATERIALS, variables: { filters: {} } },
    ],
    onCompleted: () => toast.success('Saved'),
    onError: (err) => toast.error(`Failed to save: ${err.message}`),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { toast.error('Name is required'); return }
    updateRawMaterial({
      variables: {
        id: materialId,
        data: { name: name.trim(), notes: notes.trim() || null, active, track_inventory: trackInventory },
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-5 max-w-2xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
        <span className="inline-flex items-center px-3 py-1.5 rounded bg-gray-100 text-sm text-gray-700">
          {material.unit}
        </span>
        <p className="mt-1 text-xs text-gray-500">Unit cannot be changed after creation.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="input w-full"
          placeholder="Optional notes about this ingredient"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <input
            id="active-toggle"
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600"
          />
          <label htmlFor="active-toggle" className="text-sm font-medium text-gray-700">
            Active
          </label>
        </div>

        {material.type === 'recipe' && <div
          onClick={() => setTrackInventory(!trackInventory)}
          className={`flex items-start gap-3 cursor-pointer rounded-lg border-2 p-4 transition-colors ${
            trackInventory
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <input
            id="track-inventory-toggle"
            type="checkbox"
            checked={trackInventory}
            onChange={(e) => setTrackInventory(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 mt-0.5"
          />
          <div>
            <label htmlFor="track-inventory-toggle" className={`text-sm font-semibold cursor-pointer ${trackInventory ? 'text-blue-900' : 'text-gray-700'}`}>
              Track inventory
            </label>
            <p className={`text-xs mt-0.5 ${trackInventory ? 'text-blue-700' : 'text-gray-500'}`}>
              This recipe will appear in the End of Shift log. Staff can record what was cooked, stored, wasted, and sold each day.
            </p>
          </div>
        </div>}
      </div>

      <div className="pt-2">
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

/* ─── Pricing Tab ───────────────────────────────────────────────────────────── */

const PricingTab = ({ materialId, material }: { materialId: number; material: RawMaterial }) => {
  const [priceEuros, setPriceEuros] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')

  const { data: historyData, loading: historyLoading } = useQuery(GET_RAW_MATERIAL_PRICE_HISTORY, {
    variables: { rawMaterialId: materialId },
  })

  const [setPrice, { loading: saving }] = useMutation(SET_RAW_MATERIAL_PRICE, {
    refetchQueries: [
      { query: GET_RAW_MATERIAL_PRICE_HISTORY, variables: { rawMaterialId: materialId } },
      { query: GET_RAW_MATERIAL, variables: { id: materialId } },
      { query: GET_RAW_MATERIALS, variables: { filters: {} } },
    ],
    onCompleted: () => {
      toast.success('Price updated')
      setPriceEuros('')
      setEffectiveFrom('')
    },
    onError: (err) => toast.error(`Failed: ${err.message}`),
  })

  const handleSetPrice = (e: React.FormEvent) => {
    e.preventDefault()
    const euros = parseFloat(priceEuros)
    if (isNaN(euros) || euros < 0) { toast.error('Enter a valid price'); return }
    setPrice({
      variables: {
        id: materialId,
        data: {
          cost_per_unit_millicents: eurosToMillicents(euros),
          effective_from: effectiveFrom || undefined,
        },
      },
    })
  }

  const history: RawMaterialPriceEntry[] = historyData?.rawMaterialPriceHistory ?? []

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Current price */}
      <div className="card p-6">
        <h3 className="text-sm font-medium text-gray-500 mb-1">Current cost per {material.unit}</h3>
        <p className="text-3xl font-bold text-gray-900">
          {formatCost(material.current_cost_per_unit_millicents)}
        </p>
        {material.current_cost_per_unit_millicents !== null && (
          <p className="mt-1 text-xs text-gray-400">
            = €{millicentsToEuros(material.current_cost_per_unit_millicents).toFixed(6)} per {material.unit}
          </p>
        )}
      </div>

      {/* Set new price */}
      <div className="card p-6 space-y-4">
        <h3 className="text-base font-medium text-gray-900">Set New Price</h3>
        <p className="text-sm text-gray-500">
          This adds a new entry to the price history. Historical orders are not affected.
        </p>
        <form onSubmit={handleSetPrice} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New cost per {material.unit} (€)
            </label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
              <input
                type="number"
                min="0"
                step="0.000001"
                value={priceEuros}
                onChange={(e) => setPriceEuros(e.target.value)}
                placeholder="0.000000"
                className="input pl-7 w-full"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Effective from (optional)
            </label>
            <input
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="input max-w-xs"
            />
            <p className="mt-1 text-xs text-gray-500">Leave blank to apply immediately.</p>
          </div>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : 'Set Price'}
          </button>
        </form>
      </div>

      {/* Price history */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">Price History</h3>
        </div>
        {historyLoading ? (
          <div className="p-6 flex justify-center">
            <LoadingSpinner size="sm" />
          </div>
        ) : history.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">No price history yet.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {history.map((entry, idx) => (
                <tr key={entry.id} className={idx === 0 ? 'bg-green-50' : ''}>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    {formatCost(entry.cost_per_unit_millicents)}
                    {idx === 0 && (
                      <span className="ml-2 text-xs text-green-700 font-normal">current</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{formatDate(entry.effective_from)}</td>
                  <td className="px-6 py-3 text-sm text-gray-500">{formatDate(entry.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default RawMaterialDetailPage
